import { useEffect, useState } from "react";
import { X, Gift, Copy, Check, Mail } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const STORAGE_KEY = "dulceria_exit_intent_v1";

export default function ExitIntentPopup() {
    const [visible, setVisible] = useState(false);
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [code, setCode] = useState("");
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (localStorage.getItem(STORAGE_KEY)) return;

        let triggered = false;
        const trigger = () => {
            if (triggered) return;
            triggered = true;
            setVisible(true);
        };

        const onMouseLeave = (e) => {
            if (e.clientY <= 0) trigger();
        };

        // Mobile / fallback trigger after 45s of any-page time
        const timer = setTimeout(trigger, 45_000);

        document.addEventListener("mouseleave", onMouseLeave);
        return () => {
            document.removeEventListener("mouseleave", onMouseLeave);
            clearTimeout(timer);
        };
    }, []);

    const close = () => {
        setVisible(false);
        localStorage.setItem(STORAGE_KEY, "1");
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/newsletter/subscribe", { email, source: "exit_intent" });
            setCode(data.code);
            localStorage.setItem(STORAGE_KEY, "1");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    const copyCode = async () => {
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            toast.success("Código copiado");
            setTimeout(() => setCopied(false), 2000);
        } catch {}
    };

    if (!visible) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" data-testid="exit-intent-popup">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close} />
            <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-fade-up">
                <div className="fiesta-stripes h-2" />
                <button
                    data-testid="exit-intent-close"
                    onClick={close}
                    className="absolute top-3 right-3 p-1.5 rounded-full bg-white/80 hover:bg-white shadow"
                >
                    <X className="w-4 h-4" />
                </button>

                {!code ? (
                    <div className="p-7 text-center">
                        <div className="w-16 h-16 mx-auto rounded-2xl bg-mexyellow flex items-center justify-center mb-4 animate-wobble">
                            <Gift className="w-8 h-8 text-cocoa" strokeWidth={2.2} />
                        </div>
                        <h3 className="font-display text-2xl md:text-3xl font-semibold text-cocoa mb-2">
                            ¡Espera! Te regalamos un <span className="text-cocoa">5% off</span> 🌶️
                        </h3>
                        <p className="text-muted-foreground text-sm mb-5">
                            Suscríbete y recibe tu cupón al instante. <strong>Válido en tu próximo pedido.</strong>
                        </p>
                        <form onSubmit={onSubmit} className="space-y-3">
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <input
                                    data-testid="exit-intent-email"
                                    type="email"
                                    required
                                    placeholder="tu@correo.cl"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full pl-9 pr-4 py-3 rounded-full border border-border focus:border-mexred focus:ring-2 focus:ring-mexred/20 outline-none text-sm"
                                />
                            </div>
                            <button
                                data-testid="exit-intent-submit"
                                disabled={submitting}
                                className="w-full bg-mexred hover:bg-mexred_hover disabled:bg-muted text-cocoa rounded-full py-3 font-semibold shadow-md hover:shadow-lg transition-all"
                            >
                                {submitting ? "Generando cupón..." : "Quiero mi 5% off"}
                            </button>
                            <button
                                type="button"
                                onClick={close}
                                className="w-full text-xs text-muted-foreground hover:text-cocoa underline"
                            >
                                No, gracias. Pagaré precio completo.
                            </button>
                        </form>
                    </div>
                ) : (
                    <div className="p-7 text-center" data-testid="exit-intent-success">
                        <div className="w-16 h-16 mx-auto rounded-2xl bg-marigold/15 flex items-center justify-center mb-4">
                            <Check className="w-8 h-8 text-marigold" strokeWidth={3} />
                        </div>
                        <h3 className="font-display text-2xl font-semibold text-cocoa mb-2">
                            ¡Listo! Tu cupón está aquí 🎉
                        </h3>
                        <p className="text-muted-foreground text-sm mb-4">
                            Úsalo en tu próximo pedido y obtén un <strong className="text-cocoa">5% de descuento</strong>.
                        </p>
                        <div className="bg-mexyellow/30 border-2 border-dashed border-mexyellow rounded-2xl p-4 mb-4">
                            <div className="text-xs uppercase text-cocoa/70 tracking-widest mb-1">Tu código</div>
                            <div className="font-mono font-bold text-2xl text-cocoa mb-2 select-all" data-testid="exit-intent-code">
                                {code}
                            </div>
                            <button
                                onClick={copyCode}
                                className="bg-cocoa text-white rounded-full px-4 py-1.5 text-sm font-semibold inline-flex items-center gap-2"
                            >
                                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                {copied ? "Copiado" : "Copiar código"}
                            </button>
                        </div>
                        <button
                            onClick={close}
                            className="bg-mexred text-cocoa rounded-full px-6 py-2.5 font-semibold w-full"
                        >
                            ¡A comprar dulces!
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
